

import React, { useState, useEffect, useContext } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import {
  FiUsers, FiHome, FiAlertTriangle, FiCalendar,
  FiArrowRight, FiMapPin, FiMoreVertical
} from 'react-icons/fi';
import '../../../assets/css/admin/dashboard/admindashboard/admindashboard.css';
import AdminMasterLayout from 'layouts/AdminMasterLayout';
import { useNavigate } from 'react-router-dom';
import AppContext from 'context/AppContext';
import axios from 'axios';

// ── Helpers ──────────────────────────────────────────────────
const renderStars = (r) =>
  Array(5).fill(0).map((_, i) => (
    <span key={i} style={{ color: i < r ? '#fbbf24' : '#e0e0e0', fontSize: 16 }}>★</span>
  ));

const StatusBadge = ({ status }) => {
  const map = {
    Pending: { bg: '#fef3c7', color: '#92400e' },
    'In Progress': { bg: '#dbeafe', color: '#1e40af' },
    'In Review': { bg: '#dbeafe', color: '#1e40af' },
    Resolved: { bg: '#d1fae5', color: '#065f46' },
    Approved: { bg: '#d1fae5', color: '#065f46' },
    Blocked: { bg: '#fee2e2', color: '#991b1b' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span className="adm-badge" style={{ background: s.bg, color: s.color }}>
      {status}
    </span>
  );
};


// ── Component ────────────────────────────────────────────────
const AdminDashboard = () => {
  const { baseUrl, baseUrlForFiles, authToken, Navigate } = useContext(AppContext);
  const [latestSellers, setLatestSellers] = useState([]);
  const [latestBuyers, setLatestBuyers] = useState([]);
  const [kycRequests, setKycRequests] = useState([]);
  const [latestDispute, setLatestDispute] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalBuyers: 0,
    totalSellers: 0,
    totalDisputes: 0,
    totalBookings: 0,

    buyersPercentage: 0,
    sellersPercentage: 0,
    disputesPercentage: 0,
    bookingsPercentage: 0,
  });
  const [animateCards, setAnimateCards] = useState(false);
  const [userGrowth] = useState(65);

  // ── state Card ────────────────────────────────────────────────
  const getDashboardStats = () => {
    axios
      .get(`${baseUrl}/admin/dashboard/dashboardStats`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => {
        console.log(response.data);

        setDashboardStats({
          totalBuyers: response.data.totalBuyers,
          totalSellers: response.data.totalSellers,
          totalDisputes: response.data.totalDisputes,
          totalBookings: response.data.totalBookings,

          buyersPercentage: response.data.buyersPercentage,
          sellersPercentage: response.data.sellersPercentage,
          disputesPercentage: response.data.disputesPercentage,
          bookingsPercentage: response.data.bookingsPercentage,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── view all sellers ────────────────────────────────────────────────
  const getLatestSellers = () => {
    axios
      .get(`${baseUrl}/admin/dashboard/latestSellers`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => {
        console.log(response.data);
        setLatestSellers(response.data.sellers);
      })
      .catch((error) => {
        console.log('Error fetching properties:', error);
      });
  };
  // ── view all kyc requests ────────────────────────────────────────────────
  const getKycRequests = () => {
    axios
      .get(`${baseUrl}/admin/dashboard/latestKycRequests`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => {
        console.log(response.data);
        setKycRequests(response.data.kyc);
      })
      .catch((error) => {
        console.log('Error fetching properties:', error);
      });
  };
  // ── view all buyers ────────────────────────────────────────────────
  const getLatestBuyers = () => {
    axios
      .get(`${baseUrl}/admin/dashboard/latestBuyers`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => {
        console.log(response.data);
        setLatestBuyers(response.data.buyers);
      })
      .catch((error) => {
        console.log('Error fetching properties:', error);
      });
  };
  // ── view all latest disputes ────────────────────────────────────────────────
  const getLatestDisputes = () => {
    axios
      .get(`${baseUrl}/admin/dashboard/latestDispute`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => {
        console.log(response.data);
        setLatestDispute(response.data.dispute);
      })
      .catch((error) => {
        console.log('Error fetching properties:', error);
      });
  };

  useEffect(() => {
    setAnimateCards(true);
    getDashboardStats();
    getLatestSellers();
    getLatestBuyers();
    getKycRequests();
    getLatestDisputes();
  }, []);

  const statsData = [
    {
      id: 1,
      title: "Total Buyers",
      value: dashboardStats.totalBuyers,
      change: `${dashboardStats.buyersPercentage}%`,
      changeText: "vs last month",
      icon: <FiUsers />,
      color: "#3b82f6",
      bg: "#eff6ff",
    },
    {
      id: 2,
      title: "Total Sellers",
      value: dashboardStats.totalSellers,
      change: `${dashboardStats.sellersPercentage}%`,
      changeText: "vs last month",
      icon: <FiUsers />,
      color: "#10b981",
      bg: "#ecfdf5",
    },
    {
      id: 3,
      title: "Total Disputes",
      value: dashboardStats.totalDisputes,
      change: `${dashboardStats.disputesPercentage}%`,
      changeText: "vs last month",
      icon: <FiAlertTriangle />,
      color: "#ef4444",
      bg: "#fef2f2",
      negative: true,
    },
    {
      id: 4,
      title: "Total Bookings",
      value: dashboardStats.totalBookings,
      change: `${dashboardStats.bookingsPercentage}%`,
      changeText: "vs last month",
      icon: <FiCalendar />,
      color: "#f59e0b",
      bg: "#fffbeb",
    },
  ];

  return (
    <AdminMasterLayout pageTitle='Dashboard'>
      <div className="adm-page">

        {/* ══ STAT CARDS ══════════════════════════════════ */}
        <div className="adm-stats-grid">
          {statsData.map((s, i) => (
            <div
              key={s.id}
              className={`adm-stat-card ${animateCards ? 'adm-animate' : ''}`}
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="adm-stat-top">
                <div className="adm-stat-icon-wrap" style={{ background: s.bg }}>
                  <span className="adm-stat-icon" style={{ color: s.color }}>{s.icon}</span>
                </div>
                {/* <button className="adm-dots-btn"><FiMoreVertical size={16} /></button> */}
              </div>
              <p className="adm-stat-label">{s.title}</p>
              <h2 className="adm-stat-value">{s.value}</h2>
              <div className="adm-stat-change">
                <span className={`adm-change-num ${s.negative ? 'adm-change-neg' : 'adm-change-pos'}`}>
                  {s.change}
                </span>
                <span className="adm-change-text">{s.changeText}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ══ MAIN GRID ════════════════════════════════════ */}
        <div className="adm-main-grid">

          {/* ── LEFT COLUMN ─────────────────────────────── */}
          <div className="adm-left">
            {/* Recently Added Properties */}
            <div className="adm-card">
              <div className="adm-section-header">
                <h3 className="adm-section-title">Recently Added Properties by Sellers</h3>
                <button className="adm-view-all-btn" onClick={() => Navigate('/admin/sellers')}>
                  View All <FiArrowRight size={14} />
                </button>
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Seller</th>
                      <th>Added On</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestSellers.map((p) => (
                      <tr key={p.id} className="adm-table-row">
                        <td>
                          <div className="adm-prop-cell">
                            <img src={`${baseUrlForFiles}/${p.exproperties[0]?.expropertyimages[0]?.images}`} alt={p.property_title} className="adm-prop-thumb" />
                            <div>
                              <p className="adm-prop-name">{p.exproperties[0]?.property_title}</p>
                              <p className="adm-prop-loc"><FiMapPin size={11} /> {p.exproperties[0]?.location}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="adm-seller-cell">
                            <img src={`${baseUrlForFiles}${p?.profileImage}`} alt={p.firstName} className="adm-seller-av" />
                            <div>
                              <p className="adm-seller-name">{p?.firstName} {p?.lastName}</p>
                              <p className="adm-seller-email">{p.email}</p>
                            </div>
                          </div>
                        </td>
                        <td><span className="adm-time-text">{new Date(p.created_at).toLocaleDateString()}</span></td>
                        <td><span className="adm-price-text">{p.exproperties[0]?.price}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recently Registered Buyers */}
            <div className="adm-card">
              <div className="adm-section-header">
                <h3 className="adm-section-title">Recently Registered Buyers</h3>
                <button className="adm-view-all-btn" onClick={() => Navigate('/admin/buyer')}>
                  View All <FiArrowRight size={14} />
                </button>
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Buyer</th>
                      <th>Email</th>
                      <th>Registered On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestBuyers.map((b) => (
                      <tr key={b.id} className="adm-table-row">
                        <td>
                          <div className="adm-seller-cell">
                            <img src={`${baseUrlForFiles}${b?.profileImage}`} alt={b.firstName} className="adm-seller-av" />
                            <span className="adm-seller-name">{b.firstName}{b.lastName}</span>
                          </div>
                        </td>
                        <td><span className="adm-seller-email">{b.email}</span></td>
                        <td><span className="adm-time-text">{new Date(b.created_at).toLocaleDateString()}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
          {/* /adm-left */}

          {/* ── RIGHT COLUMN ────────────────────────────── */}
          <div className="adm-right">
            {/* Latest Disputes */}
            <div className="adm-card">
              <div className="adm-section-header">
                <h3 className="adm-section-title">Latest Disputes</h3>
                <button className="adm-view-all-btn" onClick={() => Navigate('/admin/dispute')}>
                  View All <FiArrowRight size={14} />
                </button>
              </div>
              <div className="adm-list">
                {latestDispute.map((d) => {
                  const user = d.exbuyers || d.exsellers;

                  return (
                    <div key={d.id} className="adm-dispute-item">
                      <img
                        src={`${baseUrlForFiles}${user?.profileImage || ''}`}
                        alt={user?.firstName || 'User'}
                        className="adm-kyc-av"
                      />
                      <div className="adm-dispute-info">
                        <p className="adm-dispute-name">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="adm-dispute-issue">
                          {d?.category}
                        </p>
                        <p className="adm-dispute-time">
                          {new Date(d?.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <StatusBadge status={d.status} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent KYC Requests */}
            <div className="adm-card">
              <div className="adm-section-header">
                <h3 className="adm-section-title">Recent KYC Requests</h3>
                <button className="adm-view-all-btn" onClick={() => Navigate('/admin/adminkyc')}>
                  View All <FiArrowRight size={14} />
                </button>
              </div>
              <div className="adm-list">
                {kycRequests.map((k) => (
                  <div key={k.id} className="adm-kyc-item">
                    <img src={`${baseUrlForFiles}${k.exseller?.profileImage}`} alt={k.firstName} className="adm-kyc-av" />
                    <div className="adm-kyc-info">
                      <p className="adm-kyc-name">{k.exseller?.firstName} {k.exseller?.lastName}</p>
                      <p className="adm-kyc-email">{k.exseller?.email}</p>
                    </div>
                    <div className="adm-kyc-right">
                      <p className="adm-kyc-time">{new Date(k.created_at).toLocaleDateString()}</p>
                      <StatusBadge status={k.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>

      </div>
    </AdminMasterLayout>
  );
};

export default AdminDashboard;